/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FlexRow, FlexCol, TextField, FauxTextField } from 'sarsaparilla';
import get from 'lodash/get';
import SelectInput from './inputs/SelectInput';
import { PassTypeProps, VehicleInfoProps, VehicleInfoDefaults } from '../props';

export default class VehicleInfoForm extends Component {
    static defaultProps = {
        vehicleInfo: VehicleInfoDefaults,
    };

    static propTypes = {
        isScannedPass: PropTypes.bool,
        number: PropTypes.number,
        submitAttempted: PropTypes.bool,
        vehicleInfo: VehicleInfoProps,
        passType: PassTypeProps,
        onChange: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this.state = {
            vehicleType: this.props.vehicleInfo?.type || '',
            licenseNumber: get(this.props, 'vehicleInfo.fields.license', ''),
        };
    }

    componentDidMount() {
        this.props.onChange(
            {
                ...this.props.vehicleInfo,
                type: this.state.vehicleType,
                fields: { license: this.state.licenseNumber },
            },
            this.formIsValid()
        );
    }

    componentWillUnmount() {
        this.props.onChange(this.props.vehicleInfo, true);
    }

    onVehicleTypeChanged = (newVehicleType) => {
        const { vehicleInfo } = this.props;
        const { vehicleType } = this.state;
        if (newVehicleType !== vehicleType) {
            const licenseNumber = this.getDefaultLicenseNumber(newVehicleType);

            this.setState(
                (prevState) => ({
                    ...prevState,
                    vehicleType: newVehicleType,
                    licenseNumber,
                }),
                () => {
                    this.props.onChange(
                        {
                            ...vehicleInfo,
                            type: newVehicleType,
                            fields: { license: licenseNumber },
                        },
                        this.formIsValid()
                    );
                }
            );
        }
    };

    onLicenseNumberChanged = (licenseNumber) => {
        const { vehicleInfo } = this.props;

        this.setState(
            (prevState) => ({ ...prevState, licenseNumber }),
            () => {
                this.props.onChange(
                    { ...vehicleInfo, fields: { license: licenseNumber } },
                    this.formIsValid()
                );
            }
        );
    };

    getDefaultLicenseNumber = (vehicleType) => {
        const { license_required } = get(
            this.props,
            `passType.features['equipment-info']['vehicle']['${vehicleType}']`,
            {}
        );
        return license_required ? '' : 'N/A';
    };

    isMissingLicenseNumber(number, vehicleType) {
        const licenseRequired = get(this.props.passType, [
            'features',
            'equipment-info',
            'vehicle',
            vehicleType,
            'license_required',
        ]);

        if (!licenseRequired) {
            return false;
        }

        return number === '';
    }

    formIsValid() {
        const { vehicleType, licenseNumber } = this.state;

        if (!vehicleType) {
            return false;
        }

        return !this.isMissingLicenseNumber(licenseNumber, vehicleType);
    }

    render() {
        const { isScannedPass, number, passType, submitAttempted } = this.props;
        let { vehicleType } = this.state;
        let { licenseNumber } = this.state;
        const vehicleTypes = get(passType, ['features', 'equipment-info', 'vehicle']);

        if (!vehicleTypes) {
            return null;
        }

        const hasOneVehicle = Object.keys(vehicleTypes).length === 1;

        if (hasOneVehicle && !vehicleType) {
            vehicleType = Object.keys(vehicleTypes)[0];
            this.onVehicleTypeChanged(vehicleType);
        }

        if (vehicleType && !licenseNumber) {
            licenseNumber = this.getDefaultLicenseNumber(vehicleType);
        }

        const vehicleInfo = vehicleTypes[vehicleType] || {};
        const licenseRequired = get(vehicleInfo, 'license_required');

        const fauxTextVehicleLabel = vehicleInfo?.name;
        return (
            <FlexRow className={isScannedPass ? 'scanned-pass' : ''}>
                <FlexCol xl="auto">
                    {hasOneVehicle ? (
                        <FauxTextField
                            label="Vehicle Type"
                            value={fauxTextVehicleLabel}
                            className="mb-3"
                            isRequired
                        />
                    ) : (
                        <SelectInput
                            required
                            label="Vehicle Type"
                            id={`vehicle-type-${number}`}
                            value={vehicleType}
                            placeholder="Select Vehicle Type"
                            options={[
                                ...Object.entries(vehicleTypes).map(
                                    ([value, { name: label }]) => ({ label, value })
                                ),
                            ]}
                            onChange={this.onVehicleTypeChanged}
                            disabled={isScannedPass}
                            isValid={!(submitAttempted && !vehicleType)}
                        />
                    )}
                </FlexCol>
                <FlexCol xl="auto">
                    <TextField
                        isRequired={licenseRequired}
                        label="License Plate Number"
                        id={`vehicle-license-${number}`}
                        data-testid={`vehicle-license-${number}`}
                        value={licenseNumber}
                        disabled={isScannedPass || !licenseRequired}
                        shouldValidateOnUpdate={submitAttempted}
                        onChange={(e) => this.onLicenseNumberChanged(e.target.value)}
                    />
                </FlexCol>
            </FlexRow>
        );
    }
}

// cspell:ignore testid
